import React, {
  forwardRef,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";

import styles from "./HorizontalTabs.module.css";
import ResponseLoader from "../ResponseLoader/ResponseLoader";

const ArrowButton = ({ direction, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === "left" ? "Scroll tabs left" : "Scroll tabs right"}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "1.8rem",
      height: "1.8rem",
      flexShrink: 0,
      border: "none",
      background: "transparent",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.25 : 1,
      color: "#718599",
      fontSize: "1rem",
      transition: "opacity 0.2s ease",
    }}
  >
    {direction === "left" ? "‹" : "›"}
  </button>
);

/**
 * HorizontalTabs component to be used to show the tabs horizontally.
 * @param {Object[]} tabs - The tabs to be shown in the tabs.
 * @param {React.ReactNode} tabs[].header - The header of the tab.
 * @param {React.ReactNode} tabs[].children - The children to be shown when the tab is active.
 * @param {string} [tabs[].url] - The url to be used when actAsNavBar is true.
 * @param {string} [tabs[].tabUnderlineColor] - Optional underline color for the active tab.
 * @param {React.ReactNode} [tabs[].headerRightComponent] - An optional component to be shown on the right side of the header.
 * @param {Object[]} [rightTabs] - Optional tabs shown on the right side of the header, styled like the normal tabs.
 * @param {React.ReactNode} rightTabs[].header - The label of the right tab.
 * @param {React.ReactNode} [rightTabs[].children] - Content shown when the right tab's url is active.
 * @param {string} [rightTabs[].url] - If given, the right tab renders as a NavLink to this url.
 * @param {Function} [rightTabs[].onClick] - Called when the right tab is clicked.
 * @param {boolean} [rightTabs[].isActive] - Marks the right tab as active (button mode only).
 * @param {string} [rightTabs[].tabUnderlineColor] - Optional underline color.
 * @param {boolean} [actAsNavBar] - The flag to be used to act as a nav bar.
 * @param {number} [initialTabIndex=0] - The initial active tab index default is 0.
 * @param {Function} [onTabChange] - The callback to be called when the tab is changed.
 * @returns {React.ReactNode} - The HorizontalTabs component.
 * @example
 * <HorizontalTabs
 * tabs={[
 * { header: "Overview", children: <Overview />, url: "/overview" },
 * { header: "System", children: <System />, url: "/system" },
 * ]}
 * rightTabs={[
 * { header: "Resources", children: <Resources />, url: "/resources" },
 * ]}
 * initialTabIndex={1}
 * onTabChange={(index) => console.log(index)}
 * />
 */
const HorizontalTabs = forwardRef(
  (
    {
      tabs,
      initialTabIndex = 0,
      onTabChange,
      actAsNavBar,
      containerProps,
      rightTabs,
    },
    ref,
  ) => {
    const [activeTab, setActiveTab] = useState(initialTabIndex);
    const location = useLocation();
    const navigate = useNavigate();

    // A right tab whose route is currently open (nav-bar mode only).
    const activeRightTab = Array.isArray(rightTabs)
      ? rightTabs.find(
          ({ url }) =>
            url &&
            (location.pathname === url ||
              location.pathname.startsWith(`${url}/`)),
        )
      : undefined;

    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = () => {
      const el = scrollRef.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(
        Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth,
      );
    };

    useEffect(() => {
      updateScrollState();

      const el = scrollRef.current;
      if (!el) return;
      let frameId = null;
      const scheduleScrollStateUpdate = () => {
        if (frameId !== null) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          frameId = null;
          updateScrollState();
        });
      };

      const handleScroll = scheduleScrollStateUpdate;
      el.addEventListener("scroll", handleScroll);

      const resizeObserver = new ResizeObserver(scheduleScrollStateUpdate);
      resizeObserver.observe(el);

      window.addEventListener("resize", scheduleScrollStateUpdate);

      return () => {
        el.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
        window.removeEventListener("resize", scheduleScrollStateUpdate);
        if (frameId !== null) cancelAnimationFrame(frameId);
      };
    }, [tabs]);

    const scrollByAmount = (amount) => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollBy({ left: amount, behavior: "smooth" });
    };

    useEffect(() => {
      if (initialTabIndex !== activeTab) {
        setActiveTab(initialTabIndex);
      }
    }, [initialTabIndex]);

    useEffect(() => {
      if (onTabChange) onTabChange(activeTab);
    }, [activeTab, onTabChange]);

    // Keep the active tab in sync with the route. Right-tab routes are valid
    // routes, so they must not trigger the redirect to the active left tab.
    useEffect(() => {
      if (!actAsNavBar) return;
      if (activeRightTab) return;

      const foundIndex = tabs.findIndex((tab) =>
        location.pathname.startsWith(tab.url),
      );

      if (foundIndex !== -1) {
        if (foundIndex !== activeTab) setActiveTab(foundIndex);
      } else if (tabs[activeTab]?.url) {
        navigate(tabs[activeTab].url, { replace: true });
      }
    }, [
      location.pathname,
      tabs,
      actAsNavBar,
      activeTab,
      navigate,
      activeRightTab,
    ]);

    const handleTabClick = (index) => {
      setActiveTab(index);
    };

    const Children = activeRightTab
      ? activeRightTab.children
      : tabs[activeTab]?.children;
    const ActiveHeaderRightComponent = tabs[activeTab]?.headerRightComponent;

    return (
      <div
        ref={ref}
        {...containerProps}
        className={[
          styles.horizontalTabsContainer,
          containerProps?.className,
        ].join(" ")}
      >
        <div
          className={styles.header}
          style={{ minWidth: 0, alignItems: "flex-end" }}
        >
          {canScrollLeft && (
            <ArrowButton
              direction="left"
              onClick={() => scrollByAmount(-150)}
              disabled={!canScrollLeft}
            />
          )}
          <div
            ref={scrollRef}
            className={styles.tabsContainer}
            style={{
              overflowX: "auto",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              minWidth: 0,
              flexWrap: "nowrap",
              transform: "translateY(1px)",
            }}
          >
            {tabs.map(({ header, url, tabUnderlineColor, key }, index) =>
              actAsNavBar ? (
                <NavLink
                  to={url}
                  key={key || url || `horizontal-tab-${index}`}
                  style={{
                    backgroundColor: "transparent",
                    lineHeight: "1.15",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "--tab-underline-color": tabUnderlineColor,
                    ...(index === 0 && { paddingLeft: 0 }),
                  }}
                  className={({ isActive }) =>
                    [styles.tab, isActive && styles.active]
                      .filter(Boolean)
                      .join(" ")
                  }
                  onClick={() => handleTabClick(index)}
                >
                  {header}
                </NavLink>
              ) : (
                <button
                  type="button"
                  key={key || `horizontal-tab-${index}`}
                  style={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    "--tab-underline-color": tabUnderlineColor,
                    ...(index === 0 && { paddingLeft: 0 }),
                  }}
                  className={[
                    styles.tab,
                    activeTab === index && styles.active,
                  ].join(" ")}
                  onClick={() => handleTabClick(index)}
                >
                  {header}
                </button>
              ),
            )}
          </div>
          {canScrollRight && (
            <ArrowButton
              direction="right"
              onClick={() => scrollByAmount(150)}
              disabled={!canScrollRight}
            />
          )}
          {Array.isArray(rightTabs) && rightTabs.length > 0 && (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "flex-end",
                flexShrink: 0,
              }}
            >
              {rightTabs.map(
                (
                  { header, url, onClick, isActive, tabUnderlineColor, key },
                  index,
                ) =>
                  url ? (
                    <NavLink
                      to={url}
                      key={key || url || `right-tab-${index}`}
                      style={{
                        backgroundColor: "transparent",
                        lineHeight: "1.15",
                        whiteSpace: "nowrap",
                        "--tab-underline-color": tabUnderlineColor,
                      }}
                      className={({ isActive: linkActive }) =>
                        [styles.tab, linkActive && styles.active]
                          .filter(Boolean)
                          .join(" ")
                      }
                      onClick={onClick}
                    >
                      {header}
                    </NavLink>
                  ) : (
                    <button
                      type="button"
                      key={key || `right-tab-${index}`}
                      style={{
                        whiteSpace: "nowrap",
                        "--tab-underline-color": tabUnderlineColor,
                      }}
                      className={[styles.tab, isActive && styles.active]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={onClick}
                    >
                      {header}
                    </button>
                  ),
              )}
            </div>
          )}
          {ActiveHeaderRightComponent && (
            <div className={styles.headerRight}>
              {ActiveHeaderRightComponent}
            </div>
          )}
        </div>
        <Suspense fallback={<ResponseLoader simple={true} />}>
          <div className={styles.children}>{Children}</div>
        </Suspense>
      </div>
    );
  },
);

export default HorizontalTabs;